import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { companyEditConfirmPresentation } from '@/features/company/constants'
import { TransferOptionModal, openTransferWizardInNewTab } from '@/features/transfer'
import type { PropertyEditDialogsProps } from '../types'

function isHtmlMessage(message: string): boolean {
  return /<[a-z][\s\S]*>/i.test(message.trim())
}

export function PropertyEditDialogs({
  propertyId,
  saveConfirm,
  showExitUnsavedConfirm,
  deleteConfirm,
  duplicateAddressConfirm,
  duplicateProperties,
  companyChangeConfirm,
  deleteW9Confirm,
  transferOptionOpen,
  showAddressValidationModal,
  addressValidationModalMessage,
  addressValidationModalTitle,
  onAddressValidationModalClose,
  onSaveConfirm,
  onSaveConfirmDismiss,
  onExitUnsavedSave,
  onExitUnsavedDiscard,
  onExitUnsavedStay,
  onDelete,
  onDeleteDismiss,
  onDuplicateSave,
  onDuplicateDismiss,
  onCompanyChangeOk,
  onCompanyChangeDismiss,
  onW9Delete,
  onW9DeleteDismiss,
  onTransferClose,
  onProceedStaffTransfer,
}: PropertyEditDialogsProps) {
  const addressValidationIsHtml = isHtmlMessage(addressValidationModalMessage)

  return (
    <>
      <ConfirmDialog
        isOpen={showAddressValidationModal}
        title={addressValidationModalTitle}
        message={addressValidationIsHtml ? '' : addressValidationModalMessage}
        htmlMessage={addressValidationIsHtml ? addressValidationModalMessage : undefined}
        {...companyEditConfirmPresentation}
        primaryLabel="Ok"
        onPrimary={onAddressValidationModalClose}
        secondaryLabel=""
        onSecondary={onAddressValidationModalClose}
        onDismiss={onAddressValidationModalClose}
        overlayDataTestId="property-edit-address-validation-modal-overlay"
        closeButtonDataTestId="property-edit-address-validation-modal-close-button"
        primaryButtonDataTestId="property-edit-address-validation-modal-ok-button"
      />

      <ConfirmDialog
        isOpen={saveConfirm}
        title="Save Changes?"
        message="You have made changes. Are you sure you want to save them?"
        primaryLabel="Save"
        onPrimary={onSaveConfirm}
        secondaryLabel="Cancel"
        onSecondary={onSaveConfirmDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-save-confirm-overlay"
        primaryButtonDataTestId="property-edit-save-confirm-save-button"
        secondaryButtonDataTestId="property-edit-save-confirm-cancel-button"
        closeButtonDataTestId="property-edit-save-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={showExitUnsavedConfirm}
        title="Save changes?"
        message="You have unsaved changes. Save before leaving?"
        primaryLabel="Save"
        onPrimary={onExitUnsavedSave}
        secondaryLabel="No"
        onSecondary={onExitUnsavedDiscard}
        onDismiss={onExitUnsavedStay}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-exit-unsaved-confirm-overlay"
        primaryButtonDataTestId="property-edit-exit-unsaved-confirm-save-button"
        secondaryButtonDataTestId="property-edit-exit-unsaved-confirm-no-button"
        closeButtonDataTestId="property-edit-exit-unsaved-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={deleteConfirm}
        title="Delete Property"
        message="Are you sure you want to delete this property?"
        primaryLabel="Yes"
        onPrimary={onDelete}
        secondaryLabel="No"
        onSecondary={onDeleteDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-delete-property-confirm-overlay"
        primaryButtonDataTestId="property-edit-delete-property-confirm-yes-button"
        secondaryButtonDataTestId="property-edit-delete-property-confirm-no-button"
        closeButtonDataTestId="property-edit-delete-property-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={duplicateAddressConfirm}
        title="Another property exists at this address"
        message=""
        htmlMessage={
          duplicateProperties
            .map(
              (p) =>
                `${p.name} - <a data-testid="property-edit-duplicate-property-link-${p.id}" href="/property/edit/${p.id}" target="_blank" rel="noopener noreferrer">${p.id}</a>`,
            )
            .join('<br>') +
          '<br><br>If you have confirmed that there are multiple properties at the same address, then Save and continue.'
        }
        primaryLabel="Save"
        onPrimary={onDuplicateSave}
        secondaryLabel="Cancel"
        onSecondary={onDuplicateDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-duplicate-address-confirm-overlay"
        primaryButtonDataTestId="property-edit-duplicate-address-confirm-save-button"
        secondaryButtonDataTestId="property-edit-duplicate-address-confirm-cancel-button"
        closeButtonDataTestId="property-edit-duplicate-address-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={companyChangeConfirm}
        title="Company Change"
        message="Be sure to update all contacts linked to have the proper Company and Emails assigned."
        primaryLabel="OK"
        onPrimary={onCompanyChangeOk}
        secondaryLabel="Cancel"
        onSecondary={onCompanyChangeDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-company-change-confirm-overlay"
        primaryButtonDataTestId="property-edit-company-change-confirm-ok-button"
        secondaryButtonDataTestId="property-edit-company-change-confirm-cancel-button"
        closeButtonDataTestId="property-edit-company-change-confirm-close-button"
      />

      <ConfirmDialog
        isOpen={deleteW9Confirm.isOpen}
        title="Remove Document"
        message="Please confirm that you really want to remove this uploaded W-9 file."
        primaryLabel="Yes"
        onPrimary={onW9Delete}
        secondaryLabel="No"
        onSecondary={onW9DeleteDismiss}
        {...companyEditConfirmPresentation}
        overlayDataTestId="property-edit-w9-remove-confirm-overlay"
        primaryButtonDataTestId="property-edit-w9-remove-confirm-delete-button"
        secondaryButtonDataTestId="property-edit-w9-remove-confirm-cancel-button"
        closeButtonDataTestId="property-edit-w9-remove-confirm-close-button"
      />

      {propertyId && (
        <TransferOptionModal
          isOpen={transferOptionOpen}
          propertyId={String(propertyId)}
          onClose={onTransferClose}
          onProceedWizard={(opt) => openTransferWizardInNewTab(String(propertyId), opt)}
          onProceedStaffTransfer={onProceedStaffTransfer}
        />
      )}
    </>
  )
}
